import type { JepxMemberInfo } from '@ebalance/proto/bffuser/v1/jepx_member_info_model_pb'
import { useTranslation } from 'react-i18next'

import {
  Box,
  TableBody,
  TableCell,
  TableColumnHeader,
  TableHeader,
  TableRoot,
  TableRow,
  Text,
} from '@enechain/ecloud-designsystem'
import { FaInboxRegular } from '@enechain/ecloud-icons'

import {
  EMPTY_PANEL_ICON_SIZE,
  EmptyPanel,
} from '~/components/common/EmptyPanel'
import { PageSection } from '~/components/common/PageSection'
import {
  tableCellStyle,
  tableHeaderCellStyle,
  tableRootCss,
  tableWrapProps,
} from '~/pages/AdminJepxMemberInfo/components/tableStyles'
import {
  formatUpdatedAt,
  resolvedNameLabel,
} from '~/pages/AdminJepxMemberInfo/models/format'

type Props = {
  /** 未設定 (= 運営がプロビジョニング前) は undefined。 */
  memberInfo: JepxMemberInfo | undefined
}

/**
 * 会員情報セクション (AC-IJM-002 / AC-IJM-003)。
 *
 * **登録・編集・削除の UI は置かない**: JEPX会員IDを自己申告で登録できると他社の
 * 会員IDで不正入札できてしまうため、 登録・変更は運営側オペレーションに限定し、
 * お客様向け画面は閲覧専用とする (jepx-member-info.md 概要節)。
 *
 * 事業者名は事業者コードから広域マスタを、 組織名は eAuth を解決した表示専用値
 * (AC-IJM-004 / AC-IJM-005)。 組織ID (内部識別子) は表示しない。
 *
 * 未設定の空状態は `EmptyPanel` (UI 辞書 `components/empty-panel.md`)。 清算画面の
 * 会員 ID 未設定 (AC-JST-004 =「AC-IJM-003 と同じ型」) と同じ部品・同じ glyph にそろえる。
 * セクションの注記は 8080 `.jm-section-note` と同じ `Text.midEmphasis`。
 */
export const JepxMemberSection = ({
  memberInfo,
}: Props): React.ReactElement => {
  const { t } = useTranslation()

  return (
    <PageSection title={t('page:adminJepxMemberInfo.member.title')}>
      <Text color="Text.midEmphasis" fontSize="sm">
        {t('page:adminJepxMemberInfo.member.note')}
      </Text>
      {memberInfo === undefined ? (
        <EmptyPanel
          description={t('page:adminJepxMemberInfo.member.emptyNote')}
          icon={
            <FaInboxRegular
              boxSize={EMPTY_PANEL_ICON_SIZE}
              aria-hidden="true"
            />
          }
          title={t('page:adminJepxMemberInfo.member.emptyTitle')}
        />
      ) : (
        <Box {...tableWrapProps}>
          <TableRoot css={tableRootCss}>
            <TableHeader>
              <TableRow>
                <TableColumnHeader style={tableHeaderCellStyle}>
                  {t('page:adminJepxMemberInfo.member.column.jepxMemberId')}
                </TableColumnHeader>
                <TableColumnHeader style={tableHeaderCellStyle}>
                  {t('page:adminJepxMemberInfo.member.column.businessName')}
                </TableColumnHeader>
                <TableColumnHeader style={tableHeaderCellStyle}>
                  {t('page:adminJepxMemberInfo.member.column.organizationName')}
                </TableColumnHeader>
                <TableColumnHeader style={tableHeaderCellStyle}>
                  {t('page:adminJepxMemberInfo.member.column.updatedAt')}
                </TableColumnHeader>
              </TableRow>
            </TableHeader>
            <TableBody>
              <TableRow>
                <TableCell style={tableCellStyle}>
                  {memberInfo.jepxMemberId}
                </TableCell>
                <TableCell style={tableCellStyle}>
                  {resolvedNameLabel(memberInfo.businessName)}
                </TableCell>
                <TableCell style={tableCellStyle}>
                  {resolvedNameLabel(memberInfo.organizationName)}
                </TableCell>
                <TableCell style={tableCellStyle}>
                  {formatUpdatedAt(memberInfo.updatedAt)}
                </TableCell>
              </TableRow>
            </TableBody>
          </TableRoot>
        </Box>
      )}
    </PageSection>
  )
}
